import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Search,
  RefreshCw,
  X,
  Trash2,
  CheckCircle,
  Mail,
  ExternalLink,
  ChevronRight,
  ChevronLeft,
  History,
} from "lucide-react";
import toast from "react-hot-toast";
import api, {
  getLostPayments,
  getLostPaymentStats,
  lookupLostPayment,
  recoverLostPayment,
  dismissLostPayment,
  getNeedsAttentionOrders,
  resendOrderConfirmationEmail,
  importLostPaymentsFromLogs,
} from "../../config/api";

const TABS = [
  { id: "list", label: "Lost Payments" },
  { id: "lookup", label: "Payment Lookup" },
  { id: "attention", label: "Needs Attention" },
];

const SOURCE_LABELS = {
  webhook: "Webhook",
  verify: "Payment Verify",
  order_create_failed: "Order Create Failed",
};

const STATUS_STYLES = {
  open: "bg-amber-100 text-amber-800",
  recovered: "bg-green-100 text-green-800",
  dismissed: "bg-gray-100 text-gray-600",
};

const formatCurrency = (paise) => {
  if (paise == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(paise / 100);
};

function resolveAmountPaise(paymentData) {
  if (!paymentData) return null;
  const raw =
    paymentData.amountPaise ??
    paymentData.razorpay?.amount ??
    paymentData.lostPayment?.amount_paise;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const emptyShipping = {
  address: "",
  city: "",
  state: "",
  postalCode: "",
  country: "India",
};

const emptyCustomer = {
  email: "",
  firstName: "",
  lastName: "",
  phone: "",
};

function OrderSnapshotPanel({ snapshot, orderItems, title = "Order details" }) {
  const items = snapshot?.orderItems || orderItems || [];
  const shipping = snapshot?.shippingAddress;
  const customer = snapshot?.customerDetails;

  if (!items.length && !shipping && !customer) {
    return (
      <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 text-sm text-gray-500">
        No order snapshot available. Items were not captured at checkout time.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <div className="bg-gray-50 px-4 py-2 border-b">
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      </div>
      <div className="p-4 space-y-4 text-sm">
        {items.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">Items</p>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500">
                  <th className="pb-1">Product</th>
                  <th className="pb-1 text-center">Qty</th>
                  <th className="pb-1 text-right">Price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item, i) => (
                  <tr key={item.variant_id || i}>
                    <td className="py-1.5 pr-2">{item.name || "Item"}</td>
                    <td className="py-1.5 text-center">{item.quantity}</td>
                    <td className="py-1.5 text-right">
                      {new Intl.NumberFormat("en-IN", {
                        style: "currency",
                        currency: "INR",
                      }).format(item.price || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(customer || shipping) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {customer && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Customer</p>
                <p className="text-gray-800">
                  {[customer.firstName, customer.lastName].filter(Boolean).join(" ") || "—"}
                </p>
                <p className="text-gray-600">{customer.email || "—"}</p>
                {customer.phone && <p className="text-gray-600">{customer.phone}</p>}
              </div>
            )}
            {shipping && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Shipping</p>
                <p className="text-gray-800">{shipping.address || "—"}</p>
                <p className="text-gray-600">
                  {[shipping.city, shipping.postalCode, shipping.country]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              </div>
            )}
          </div>
        )}
        {snapshot?.totalPrice != null && (
          <div className="flex justify-between pt-2 border-t text-sm font-medium">
            <span>Total</span>
            <span>
              {new Intl.NumberFormat("en-IN", {
                style: "currency",
                currency: "INR",
              }).format(snapshot.totalPrice)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function RecoveryModal({ open, onClose, initialPayment, onSuccess }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [paymentAmountPaise, setPaymentAmountPaise] = useState(0);
  const [amountLoading, setAmountLoading] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [productResults, setProductResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);

  const [form, setForm] = useState({
    razorpay_payment_id: "",
    razorpay_order_id: "",
    lostPaymentId: null,
    customerDetails: { ...emptyCustomer },
    shippingAddress: { ...emptyShipping },
    orderItems: [],
    taxPrice: 0,
    shippingPrice: 0,
  });

  useEffect(() => {
    if (!open || !initialPayment) {
      setPaymentAmountPaise(0);
      setAmountLoading(false);
      return;
    }

    const rzp = initialPayment.razorpay;
    const snap =
      initialPayment.orderSnapshot ||
      initialPayment.lostPayment?.metadata?.orderSnapshot ||
      null;

    const hasCompleteSnapshot =
      snap?.orderItems?.length > 0 &&
      snap?.customerDetails?.email &&
      snap?.shippingAddress?.address;

    setForm({
      razorpay_payment_id:
        initialPayment.paymentId || rzp?.id || "",
      razorpay_order_id: rzp?.order_id || initialPayment.lostPayment?.razorpay_order_id || "",
      lostPaymentId: initialPayment.lostPayment?.id || null,
      customerDetails: {
        ...emptyCustomer,
        email: snap?.customerDetails?.email || rzp?.email || initialPayment.lostPayment?.customer_email || "",
        firstName: snap?.customerDetails?.firstName || "",
        lastName: snap?.customerDetails?.lastName || "",
        phone: snap?.customerDetails?.phone || "",
      },
      shippingAddress: snap?.shippingAddress
        ? { ...emptyShipping, ...snap.shippingAddress }
        : { ...emptyShipping },
      orderItems: snap?.orderItems?.length
        ? snap.orderItems.map((item) => ({
            product_id: item.product_id,
            variant_id: item.variant_id,
            name: item.name || "Item",
            quantity: item.quantity || 1,
            price: item.price || 0,
          }))
        : [],
      taxPrice: snap?.taxPrice ?? 0,
      shippingPrice: snap?.shippingPrice ?? 0,
    });
    setStep(hasCompleteSnapshot ? 3 : 1);
    setProductSearch("");
    setProductResults([]);

    const localAmount = resolveAmountPaise(initialPayment);
    if (localAmount) {
      setPaymentAmountPaise(localAmount);
      setAmountLoading(false);
      return;
    }

    const paymentId =
      initialPayment.paymentId ||
      rzp?.id ||
      initialPayment.lostPayment?.razorpay_payment_id;
    if (!paymentId) {
      setPaymentAmountPaise(0);
      setAmountLoading(false);
      return;
    }

    let cancelled = false;
    setAmountLoading(true);
    lookupLostPayment(paymentId)
      .then((res) => {
        if (cancelled) return;
        const fetched = resolveAmountPaise(res);
        setPaymentAmountPaise(fetched || 0);
      })
      .catch(() => {
        if (!cancelled) setPaymentAmountPaise(0);
      })
      .finally(() => {
        if (!cancelled) setAmountLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, initialPayment]);

  const itemsPrice = form.orderItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );
  const totalPrice = itemsPrice + form.taxPrice + form.shippingPrice;
  const totalPaise = Math.round(totalPrice * 100);
  const totalsMatch =
    paymentAmountPaise > 0 &&
    Math.abs(totalPaise - paymentAmountPaise) <= 1;

  const searchProducts = async (term) => {
    if (!term.trim()) {
      setProductResults([]);
      return;
    }
    try {
      setSearchingProducts(true);
      const res = await api.get("/admin/products", {
        params: { search: term.trim(), limit: 8, is_active: true },
      });
      setProductResults(res.data?.data?.products || res.data?.products || []);
    } catch {
      toast.error("Product search failed");
    } finally {
      setSearchingProducts(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => searchProducts(productSearch), 400);
    return () => clearTimeout(t);
  }, [productSearch]);

  const addVariant = (product, variant) => {
    const price = parseFloat(variant.price_sale || variant.price_regular || 0);
    setForm((prev) => ({
      ...prev,
      orderItems: [
        ...prev.orderItems,
        {
          product_id: product.id,
          variant_id: variant.id,
          name: `${product.name}${variant.sku ? ` (${variant.sku})` : ""}`,
          quantity: 1,
          price,
        },
      ],
    }));
    setProductSearch("");
    setProductResults([]);
    toast.success("Item added");
  };

  const updateItem = (index, field, value) => {
    setForm((prev) => {
      const items = [...prev.orderItems];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, orderItems: items };
    });
  };

  const removeItem = (index) => {
    setForm((prev) => ({
      ...prev,
      orderItems: prev.orderItems.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    if (!totalsMatch) {
      toast.error("Order total must match the payment amount");
      return;
    }
    if (form.orderItems.length === 0) {
      toast.error("Add at least one line item");
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        razorpay_payment_id: form.razorpay_payment_id,
        razorpay_order_id: form.razorpay_order_id,
        lostPaymentId: form.lostPaymentId,
        orderItems: form.orderItems,
        customerDetails: form.customerDetails,
        shippingAddress: form.shippingAddress,
        itemsPrice,
        taxPrice: form.taxPrice,
        shippingPrice: form.shippingPrice,
        totalPrice,
      };
      const res = await recoverLostPayment(payload);
      toast.success(`Order ${res?.order_number || "created"} recovered`);
      onSuccess(res);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Recovery failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Recover Order
            </h2>
            <p className="text-sm text-gray-500">Step {step} of 4</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Verify payment details before creating the order.
              </p>
              {!initialPayment?.orderSnapshot &&
                !initialPayment?.lostPayment?.metadata?.orderSnapshot && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    No saved cart snapshot — you will need to add items manually in step 3.
                  </p>
                )}
              <div className="rounded-lg bg-gray-50 p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Payment ID</span>
                  <span className="font-mono text-xs">{form.razorpay_payment_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Razorpay Order ID</span>
                  <span className="font-mono text-xs">{form.razorpay_order_id || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Amount</span>
                  <span className="font-semibold">
                    {amountLoading
                      ? "Loading..."
                      : paymentAmountPaise > 0
                        ? formatCurrency(paymentAmountPaise)
                        : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Status</span>
                  <span className="text-green-700 font-medium">
                    {initialPayment?.razorpay?.status || "captured"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">First name</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={form.customerDetails.firstName}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        customerDetails: { ...p.customerDetails, firstName: e.target.value },
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Last name</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={form.customerDetails.lastName}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        customerDetails: { ...p.customerDetails, lastName: e.target.value },
                      }))
                    }
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={form.customerDetails.email}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      customerDetails: { ...p.customerDetails, email: e.target.value },
                    }))
                  }
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={form.customerDetails.phone}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      customerDetails: { ...p.customerDetails, phone: e.target.value },
                    }))
                  }
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Address *</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={form.shippingAddress.address}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      shippingAddress: { ...p.shippingAddress, address: e.target.value },
                    }))
                  }
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">City</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={form.shippingAddress.city}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        shippingAddress: { ...p.shippingAddress, city: e.target.value },
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={form.shippingAddress.state}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        shippingAddress: { ...p.shippingAddress, state: e.target.value },
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">PIN</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={form.shippingAddress.postalCode}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        shippingAddress: { ...p.shippingAddress, postalCode: e.target.value },
                      }))
                    }
                  />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Search products to add
                </label>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="Search by product name..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                />
                {searchingProducts && (
                  <p className="text-xs text-gray-400 mt-1">Searching...</p>
                )}
                {productResults.length > 0 && (
                  <div className="mt-2 border rounded-lg divide-y max-h-40 overflow-y-auto">
                    {productResults.map((product) =>
                      (product.product_variants || [])
                        .filter((v) => v.is_active)
                        .map((variant) => (
                          <button
                            key={variant.id}
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex justify-between"
                            onClick={() => addVariant(product, variant)}
                          >
                            <span>{product.name}</span>
                            <span className="text-gray-500">
                              ₹{variant.price_sale || variant.price_regular}
                            </span>
                          </button>
                        )),
                    )}
                  </div>
                )}
              </div>

              {form.orderItems.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No items yet. Search and add products above.
                </p>
              ) : (
                <div className="space-y-2">
                  {form.orderItems.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 border rounded-lg p-2 text-sm"
                    >
                      <span className="flex-1 truncate">{item.name}</span>
                      <input
                        type="number"
                        min="1"
                        className="w-16 border rounded px-2 py-1"
                        value={item.quantity}
                        onChange={(e) =>
                          updateItem(idx, "quantity", parseInt(e.target.value, 10) || 1)
                        }
                      />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="w-24 border rounded px-2 py-1"
                        value={item.price}
                        onChange={(e) =>
                          updateItem(idx, "price", parseFloat(e.target.value) || 0)
                        }
                      />
                      <button
                        type="button"
                        onClick={() => removeItem(idx)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Tax (₹)</label>
                  <input
                    type="number"
                    min="0"
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={form.taxPrice}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, taxPrice: parseFloat(e.target.value) || 0 }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Shipping (₹)</label>
                  <input
                    type="number"
                    min="0"
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={form.shippingPrice}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        shippingPrice: parseFloat(e.target.value) || 0,
                      }))
                    }
                  />
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3">
              <div className="rounded-lg border p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Customer</span>
                  <span>
                    {form.customerDetails.firstName} {form.customerDetails.lastName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Email</span>
                  <span>{form.customerDetails.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Items</span>
                  <span>{form.orderItems.length}</span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>Order total</span>
                  <span>{formatCurrency(totalPaise)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Payment amount</span>
                  <span>{formatCurrency(paymentAmountPaise)}</span>
                </div>
              </div>
              {!totalsMatch && (
                <p className="text-sm text-red-600 bg-red-50 rounded-lg p-3">
                  Totals do not match. Adjust line items, tax, or shipping until the order total
                  equals {formatCurrency(paymentAmountPaise)}.
                </p>
              )}
              {totalsMatch && (
                <p className="text-sm text-green-700 bg-green-50 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Totals match — ready to recover.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-between px-6 py-4 border-t bg-gray-50">
          <button
            type="button"
            disabled={step === 1}
            onClick={() => setStep((s) => s - 1)}
            className="inline-flex items-center gap-1 px-4 py-2 text-sm text-gray-700 disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          {step < 4 ? (
            <button
              type="button"
              onClick={() => setStep((s) => s + 1)}
              className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Next <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              disabled={submitting || !totalsMatch}
              onClick={handleSubmit}
              className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {submitting ? "Recovering..." : "Confirm Recovery"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const LostOrdersManagement = () => {
  const [activeTab, setActiveTab] = useState("list");
  const [stats, setStats] = useState(null);
  const [list, setList] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "open",
    source: "",
    search: "",
  });

  const [lookupId, setLookupId] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  const [attentionList, setAttentionList] = useState([]);
  const [attentionLoading, setAttentionLoading] = useState(false);

  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryPayment, setRecoveryPayment] = useState(null);
  const [importing, setImporting] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await getLostPaymentStats();
      setStats(res || null);
    } catch {
      /* non-fatal */
    }
  }, []);

  const fetchList = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = { page, limit: 20, ...filters };
      if (!params.status) delete params.status;
      if (!params.source) delete params.source;
      if (!params.search) delete params.search;
      const res = await getLostPayments(params);
      setList(res?.list || []);
      setPagination(res?.pagination || { page: 1, totalPages: 1, total: 0 });
    } catch {
      toast.error("Failed to load lost payments");
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchAttention = useCallback(async () => {
    try {
      setAttentionLoading(true);
      const res = await getNeedsAttentionOrders();
      setAttentionList(res?.list || []);
    } catch {
      toast.error("Failed to load orders needing attention");
    } finally {
      setAttentionLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (activeTab === "list") fetchList(1);
  }, [activeTab, fetchList]);

  useEffect(() => {
    if (activeTab === "attention") fetchAttention();
  }, [activeTab, fetchAttention]);

  const handleLookup = async (e) => {
    e?.preventDefault();
    if (!lookupId.trim()) return;
    try {
      setLookupLoading(true);
      const res = await lookupLostPayment(lookupId.trim());
      setLookupResult(res);
    } catch {
      toast.error("Lookup failed");
      setLookupResult(null);
    } finally {
      setLookupLoading(false);
    }
  };

  const openRecovery = (paymentData) => {
    setRecoveryPayment({
      ...paymentData,
      amountPaise: resolveAmountPaise(paymentData),
    });
    setRecoveryOpen(true);
  };

  const handleDismiss = async (id) => {
    const notes = window.prompt("Optional note for dismissing this case:");
    if (notes === null) return;
    try {
      await dismissLostPayment(id, notes);
      toast.success("Case dismissed");
      fetchList(pagination.page);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.message || "Dismiss failed");
    }
  };

  const handleResendEmail = async (orderId) => {
    try {
      await resendOrderConfirmationEmail(orderId);
      toast.success("Confirmation email sent");
      fetchAttention();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send email");
    }
  };

  const handleImportFromLogs = async () => {
    try {
      setImporting(true);
      const res = await importLostPaymentsFromLogs();
      const s = res || {};
      const total = (s.imported || 0) + (s.updated || 0);
      if (total === 0 && (s.foundInLogs || 0) === 0) {
        toast("No historical lost payments found in server logs", { icon: "ℹ️" });
      } else {
        toast.success(
          `Imported ${s.imported || 0} new, updated ${s.updated || 0} from logs (${s.foundInLogs || 0} found, ${s.skippedOrderExists || 0} already have orders)`,
        );
      }
      fetchList(pagination.page);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.message || "Log import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <AlertTriangle className="h-7 w-7 text-amber-500" />
          <h1 className="text-2xl font-bold text-gray-900">Lost Orders</h1>
        </div>
        <p className="text-sm text-gray-600">
          Find payments captured without a matching order and recover them manually.
          Use <strong>Import from logs</strong> to load cases recorded before this feature existed.
        </p>
      </div>

      {stats && stats.openCount > 0 && (
        <div className="mb-6 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800">
            <strong>{stats.openCount} open case{stats.openCount !== 1 ? "s" : ""}</strong>
            {" "}totalling {formatCurrency(stats.amountAtRiskPaise)} need your attention.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-4 border">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Open Cases</p>
          <p className="text-2xl font-bold text-amber-600 mt-1">{stats?.openCount ?? "—"}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Amount at Risk</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {stats ? formatCurrency(stats.amountAtRiskPaise) : "—"}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Recovered (7 days)</p>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {stats?.recoveredLast7Days ?? "—"}
          </p>
        </div>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-primary-600 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
              {tab.id === "attention" && stats?.needsEmailCount > 0 && (
                <span className="ml-2 bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full">
                  {stats.needsEmailCount}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === "list" && (
        <>
          <div className="bg-white rounded-xl shadow-sm p-4 mb-4 flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select
                className="border rounded-lg px-3 py-2 text-sm"
                value={filters.status}
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="">All</option>
                <option value="open">Open</option>
                <option value="recovered">Recovered</option>
                <option value="dismissed">Dismissed</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Source</label>
              <select
                className="border rounded-lg px-3 py-2 text-sm"
                value={filters.source}
                onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}
              >
                <option value="">All sources</option>
                <option value="webhook">Webhook</option>
                <option value="verify">Payment Verify</option>
                <option value="order_create_failed">Order Create Failed</option>
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs text-gray-500 mb-1">Search</label>
              <input
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="Payment ID, order ID, or email..."
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              />
            </div>
            <button
              type="button"
              onClick={() => fetchList(1)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              type="button"
              onClick={handleImportFromLogs}
              disabled={importing}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              title="Import lost payments recorded in server logs before this feature existed"
            >
              <History className="h-4 w-4" />
              {importing ? "Importing..." : "Import from logs"}
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {loading ? (
              <div className="flex justify-center py-16">
                <div className="h-8 w-8 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : list.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <CheckCircle className="h-10 w-10 mx-auto text-green-400 mb-3" />
                <p>No lost payment cases found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detected</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {list.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                          {formatDate(row.created_at)}
                          {row.metadata?.imported_from_logs && (
                            <span className="ml-1 inline-block px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600 rounded">
                              Historical
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">{row.razorpay_payment_id}</td>
                        <td className="px-4 py-3">{formatCurrency(row.amount_paise)}</td>
                        <td className="px-4 py-3">{row.customer_email || "—"}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {row.hasSnapshot || row.snapshotSummary?.itemCount > 0 ? (
                            <span title={row.snapshotSummary?.itemNames?.join(", ")}>
                              {row.snapshotSummary.itemCount} item
                              {row.snapshotSummary.itemCount !== 1 ? "s" : ""}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">{SOURCE_LABELS[row.source] || row.source}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[row.status] || ""}`}>
                            {row.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                          {row.status === "open" && (
                            <>
                              <button
                                type="button"
                                className="text-primary-600 hover:text-primary-800 text-xs font-medium"
                                onClick={() =>
                                  openRecovery({
                                    paymentId: row.razorpay_payment_id,
                                    razorpay: {
                                      id: row.razorpay_payment_id,
                                      order_id: row.razorpay_order_id,
                                      amount: row.amount_paise,
                                      status: "captured",
                                      email: row.customer_email,
                                    },
                                    lostPayment: row,
                                    orderSnapshot: row.metadata?.orderSnapshot,
                                  })
                                }
                              >
                                Recover
                              </button>
                              <button
                                type="button"
                                className="text-gray-500 hover:text-gray-700 text-xs font-medium"
                                onClick={() => handleDismiss(row.id)}
                              >
                                Dismiss
                              </button>
                            </>
                          )}
                          {row.status === "recovered" && row.recovered_order_id && (
                            <Link
                              to={`/admin/orders?search=${encodeURIComponent(row.recovered_order_number || row.recovered_order_id || "")}`}
                              className="text-primary-600 hover:text-primary-800 text-xs font-medium inline-flex items-center gap-1"
                            >
                              View order <ExternalLink className="h-3 w-3" />
                            </Link>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {pagination.totalPages > 1 && (
              <div className="flex justify-center gap-2 py-4 border-t">
                <button
                  type="button"
                  disabled={pagination.page <= 1}
                  onClick={() => fetchList(pagination.page - 1)}
                  className="px-3 py-1 text-sm border rounded disabled:opacity-40"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm text-gray-600">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  type="button"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => fetchList(pagination.page + 1)}
                  className="px-3 py-1 text-sm border rounded disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === "lookup" && (
        <div className="max-w-2xl">
          <form onSubmit={handleLookup} className="flex gap-2 mb-6">
            <input
              className="flex-1 border rounded-lg px-4 py-2 text-sm font-mono"
              placeholder="pay_XXXXXXXXXXXXXX"
              value={lookupId}
              onChange={(e) => setLookupId(e.target.value)}
            />
            <button
              type="submit"
              disabled={lookupLoading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              <Search className="h-4 w-4" />
              {lookupLoading ? "Looking up..." : "Lookup"}
            </button>
          </form>

          {lookupResult && (
            <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
              {lookupResult.razorpayError && !lookupResult.razorpay && (
                <p className="text-red-600 text-sm">{lookupResult.razorpayError}</p>
              )}
              {lookupResult.razorpay && (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status</span>
                    <span className="font-medium capitalize">{lookupResult.razorpay.status}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Amount</span>
                    <span>{formatCurrency(lookupResult.razorpay.amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Email</span>
                    <span>{lookupResult.razorpay.email || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Method</span>
                    <span className="capitalize">{lookupResult.razorpay.method || "—"}</span>
                  </div>
                </div>
              )}
              <div className={`rounded-lg p-3 text-sm ${lookupResult.orderExists ? "bg-green-50 text-green-800" : "bg-amber-50 text-amber-800"}`}>
                {lookupResult.orderExists
                  ? `Order exists: ${lookupResult.order?.order_number}`
                  : "No matching order found in database"}
              </div>
              {lookupResult.paymentMismatch && (
                <div className="rounded-lg p-3 text-sm bg-red-50 text-red-800 border border-red-200">
                  <strong className="capitalize">
                    {lookupResult.paymentMismatch.type.replace(/_/g, " ")}:
                  </strong>{" "}
                  {lookupResult.paymentMismatch.details}
                </div>
              )}
              <OrderSnapshotPanel
                snapshot={lookupResult.orderSnapshot}
                orderItems={lookupResult.order?.order_items}
                title={
                  lookupResult.orderExists
                    ? "Order items"
                    : "Captured checkout snapshot"
                }
              />
              {lookupResult.canRecover && (
                <button
                  type="button"
                  onClick={() => openRecovery(lookupResult)}
                  className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                >
                  Recover Order
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === "attention" && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <p className="px-4 py-3 text-sm text-gray-600 border-b">
            Paid orders where the confirmation email was never sent.
          </p>
          {attentionLoading ? (
            <div className="flex justify-center py-16">
              <div className="h-8 w-8 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : attentionList.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <CheckCircle className="h-10 w-10 mx-auto text-green-400 mb-3" />
              <p>All paid orders have confirmation emails sent.</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {attentionList.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{order.order_number}</td>
                    <td className="px-4 py-3">{order.customer_details?.email || "—"}</td>
                    <td className="px-4 py-3">
                      {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(order.total_price)}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{formatDate(order.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleResendEmail(order.id)}
                        className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800 text-xs font-medium"
                      >
                        <Mail className="h-3 w-3" /> Resend email
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <RecoveryModal
        open={recoveryOpen}
        onClose={() => setRecoveryOpen(false)}
        initialPayment={recoveryPayment}
        onSuccess={() => {
          fetchList(pagination.page);
          fetchStats();
          setLookupResult(null);
        }}
      />
    </div>
  );
};

export default LostOrdersManagement;
